/**
 * Formulaire d'authentification par OTP (email → code à 6 chiffres), réutilisable.
 *
 * Consommé par la page de connexion (`/login`) et la page de bienvenue après
 * achat (`/bienvenue`). Après `verifyOtp`, on ne navigue PAS : la redirection
 * est gérée par onAuthStateChange + router.invalidate(). `onVerified` permet à
 * l'appelant d'ajouter un comportement (ex. /bienvenue attend l'activation).
 */

import { useEffect, useState, useTransition, type ReactNode } from 'react';
import { clearOAuthReturnError, peekOAuthReturnError } from '@/lib/oauth-return-error';
import {
  Alert,
  AlertDescription,
  Button,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  OtpInput,
  SocialButton,
  useTurnstile,
} from '@jeraisonne/ui';
import { Capacitor } from '@capacitor/core';
import { supabase, supabaseUrl } from '@/lib/supabase';
import { NATIVE_OAUTH_REDIRECT_URL } from '@/lib/native-oauth';
import { isTouchPrimary } from '@/lib/platform';
import { isTestAccountEmail, loginTestAccount } from '@/lib/test-account';
import {
  OTP_VALID_MS,
  clearPendingOtp,
  otpErrorMessage,
  otpTime,
  readPendingOtp,
  reusablePendingOtp,
  savePendingOtp,
} from '@/lib/otp-pending';

const MARKETING_URL =
  (import.meta.env.VITE_MARKETING_URL as string | undefined) ?? 'https://jeraisonne.fr';

/**
 * Bundle ID iOS = utilisé comme `clientId` pour Sign In with Apple natif via
 * AuthenticationServices. Sur natif, Apple valide directement le Bundle ID
 * enregistré chez Apple Developer (pas le Service ID web). Doit correspondre
 * à `PRODUCT_BUNDLE_IDENTIFIER` du target App dans le pbxproj iOS.
 */
const IOS_BUNDLE_ID = 'fr.jeraisonne.app';

/**
 * Génère un nonce cryptographique pour Sign In with Apple.
 *
 * Le raw nonce (32 octets random hex) est passé à Supabase pour vérification.
 * Le hash SHA-256 hex est passé à Apple : Apple encode ce hash dans l'ID token,
 * Supabase re-hash le raw reçu et compare. Empêche les attaques de replay du
 * ID token. Cf. https://supabase.com/docs/guides/auth/social-login/auth-apple
 */
async function generateAppleNonce(): Promise<{ raw: string; hashed: string }> {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  const raw = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  const hashBuf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw));
  const hashed = Array.from(new Uint8Array(hashBuf), (b) => b.toString(16).padStart(2, '0')).join(
    '',
  );
  return { raw, hashed };
}

export type OtpMode = 'login' | 'signup';
export type OtpStep = 'email' | 'otp';
type OAuthProvider = 'google' | 'facebook' | 'apple';

export interface OtpAuthFormProps {
  /**
   * Mode initial : ne change que les TEXTES de l'en-tête (« Bon retour » /
   * « Créer un compte »). Dans les deux cas un email inconnu crée le compte.
   */
  initialMode?: OtpMode;
  /** Email prérempli + verrouillé (ex. achat : l'email du paiement). Force signup. */
  lockedEmail?: string;
  /** Masquer les boutons OAuth (ex. flux achat où l'email est imposé). */
  hideOAuth?: boolean;
  /** Sans effet (conservé pour compat) : il n'y a plus de bascule login ⇄ signup. */
  hideModeToggle?: boolean;
  /** Appelé après un verifyOtp réussi (avant la redirection auto). */
  onVerified?: () => void;
  /** En-tête custom rendu selon l'étape. Défaut : titres login/signup. */
  renderHeader?: (ctx: { step: OtpStep; mode: OtpMode; email: string }) => ReactNode;
}

export function OtpAuthForm({
  initialMode = 'login',
  lockedEmail,
  hideOAuth = false,
  onVerified,
  renderHeader,
}: OtpAuthFormProps) {
  const forcedSignup = lockedEmail != null;
  const mode: OtpMode = forcedSignup ? 'signup' : initialMode;
  // Un code envoyé il y a moins d'une heure (l'élève est allé le lire dans sa boîte mail, la page s'est
  // rechargée) : on revient directement sur l'écran du code, sans en renvoyer un (cf. lib/otp-pending).
  const [restored] = useState(() => {
    const p = readPendingOtp();
    if (!p || (lockedEmail != null && p.email !== lockedEmail.trim().toLowerCase())) return null;
    return p;
  });
  const [step, setStep] = useState<OtpStep>(restored ? 'otp' : 'email');
  const [email, setEmail] = useState(lockedEmail ?? restored?.email ?? '');
  const [sentAt, setSentAt] = useState<number | null>(restored?.sentAt ?? null);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<OAuthProvider | null>(null);
  // erreur d'un retour Google / Facebook / Apple (cf. lib/oauth-return-error.ts)
  const [error, setError] = useState<string | null>(() => peekOAuthReturnError());
  useEffect(() => clearOAuthReturnError(), []);
  const [resentMessage, setResentMessage] = useState<string | null>(null);
  const [isResending, startResending] = useTransition();
  // Jeton CAPTCHA (Turnstile) pour signInWithOtp — exigé par Supabase une fois
  // la protection CAPTCHA activée. Indisponible → envoi sans jeton (cf. hook).
  const turnstile = useTurnstile();

  // Connexion et inscription ne font qu'UN : un email inconnu crée le compte
  // (Supabase), l'onboarding prend le relais après le code. Plus d'erreur
  // « Signups not allowed » quand un nouveau venu se trompe de bouton.
  const shouldCreateUser = true;

  // Reset du oauthLoading dans 2 cas où le listener browserFinished ne se déclenche
  // pas de façon fiable sur iOS :
  //  1. L'utilisateur revient dans l'app depuis Safari via le bouton système
  //     "◀ TestFlight" ou "◀ App" (en haut à gauche) — le browser n'est pas
  //     techniquement "fermé", il est mis en background, donc browserFinished
  //     ne se déclenche pas.
  //  2. Le flow OAuth réussit mais le deep link fr.jeraisonne.app://auth-callback
  //     ne bascule pas iOS dans l'app (config Supabase Apple incomplète ou schema
  //     custom pas prioritaire face à Safari). L'événement SIGNED_IN de
  //     onAuthStateChange sert alors de filet de sécurité.
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setOauthLoading(null);
      }
    });

    let removeAppStateListener: (() => void) | undefined;
    if (Capacitor.isNativePlatform()) {
      void (async () => {
        const { App } = await import('@capacitor/app');
        const listener = await App.addListener('appStateChange', ({ isActive }) => {
          // Retour au foreground = user est revenu dans l'app. Si le flow OAuth
          // était en cours, il est soit fini (SIGNED_IN suivra), soit abandonné
          // (browser fermé manuellement). Dans les 2 cas, on lève le loading —
          // ça débloque le bouton pour permettre une re-tentative.
          if (isActive) setOauthLoading(null);
        });
        removeAppStateListener = () => void listener.remove();
      })();
    }

    return () => {
      subscription.unsubscribe();
      removeAppStateListener?.();
    };
  }, []);

  async function handleSendOtp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setResentMessage(null);
    setLoading(true);
    const normalized = email.trim().toLowerCase();
    try {
      // Compte de démo (revue store) : aucun mail réel n'est envoyé — le code
      // fixe est vérifié côté serveur à l'étape suivante. Cf. lib/test-account.
      if (isTestAccountEmail(email)) {
        setStep('otp');
        return;
      }
      // Un code récent pour la même adresse : on ne le remplace pas (un nouvel envoi annulerait celui que
      // l'élève a dans sa boîte mail). « Renvoyer le code » reste là s'il ne l'a pas reçu.
      const reuse = reusablePendingOtp(normalized);
      if (reuse) {
        setSentAt(reuse.sentAt);
        setResentMessage(
          `Un code t'a déjà été envoyé à ${otpTime(reuse.sentAt)} : il est toujours valable. Pas reçu ? Touche « Renvoyer le code ».`,
        );
        setStep('otp');
        return;
      }
      const captchaToken = await turnstile.getToken();
      const { error: err } = await supabase.auth.signInWithOtp({
        email: normalized,
        options: { shouldCreateUser, captchaToken },
      });
      if (err) throw err;
      const now = Date.now();
      savePendingOtp(normalized, now);
      setSentAt(now);
      setStep('otp');
    } catch (err) {
      setError(otpErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyOtp(code: string) {
    if (code.length !== 6) return;
    setError(null);
    setLoading(true);
    try {
      // Compte de démo (revue store) : la session est frappée par l'edge fn.
      if (isTestAccountEmail(email)) {
        const ok = await loginTestAccount(code);
        if (!ok) throw new Error('Code invalide.');
        onVerified?.();
        return;
      }
      const { error: err } = await supabase.auth.verifyOtp({
        email: email.trim().toLowerCase(),
        token: code,
        type: 'email',
      });
      if (err) throw err;
      clearPendingOtp();
      onVerified?.();
      // Pas de navigate : onAuthStateChange → router.invalidate() gère la suite.
    } catch (err) {
      setError(otpErrorMessage(err));
      setLoading(false);
    }
  }

  function handleResend() {
    setResentMessage(null);
    // Compte de démo : le code est fixe (123456), il n'y a rien à renvoyer —
    // et surtout pas de vrai signInWithOtp (court-circuiterait le flow de test
    // en déclenchant un envoi réel non lu par le relecteur).
    if (isTestAccountEmail(email)) {
      setResentMessage('Le code de ce compte de démonstration est toujours 123456.');
      return;
    }
    startResending(async () => {
      const normalized = email.trim().toLowerCase();
      const captchaToken = await turnstile.getToken();
      const { error: err } = await supabase.auth.signInWithOtp({
        email: normalized,
        options: { shouldCreateUser, captchaToken },
      });
      if (err) {
        setResentMessage(otpErrorMessage(err));
        return;
      }
      const now = Date.now();
      savePendingOtp(normalized, now);
      setSentAt(now);
      setError(null);
      setOtp('');
      setResentMessage(
        `Nouveau code envoyé à ${otpTime(now)}. L'ancien ne marche plus : prends celui de ce dernier email.`,
      );
    });
  }

  // Sign in with Apple sur iOS = flow NATIF via AuthenticationServices
  // (@capacitor-community/apple-sign-in). Apple exige ce chemin sur iOS et
  // rejette systématiquement les apps qui passent par un OAuth Browser popup
  // (Guideline 2.1(a) — expérience de chargement infini). Aucun deep link
  // nécessaire : le plugin renvoie directement un ID token qu'on échange
  // contre une session Supabase.
  async function handleAppleSignInNativeIOS() {
    try {
      const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');
      const { raw: rawNonce, hashed: hashedNonce } = await generateAppleNonce();
      const result = await SignInWithApple.authorize({
        clientId: IOS_BUNDLE_ID,
        // Apple exige un redirectURI même en natif — non utilisé pour le retour
        // (le plugin résout la promesse directement), mais Apple le valide
        // contre les Return URLs déclarées dans le Services ID Apple Developer.
        // On pointe vers l'endpoint OAuth Supabase pour cohérence avec la
        // config web (déjà autorisée côté Apple).
        redirectURI: `${supabaseUrl}/auth/v1/callback`,
        scopes: 'email name',
        nonce: hashedNonce,
      });
      const idToken = result.response.identityToken;
      if (!idToken) {
        setOauthLoading(null);
        setError('Réponse Apple incomplète (identityToken absent).');
        return;
      }
      const { error: signInErr } = await supabase.auth.signInWithIdToken({
        provider: 'apple',
        token: idToken,
        nonce: rawNonce,
      });
      if (signInErr) {
        setOauthLoading(null);
        setError(`Connexion Apple refusée : ${signInErr.message}`);
        return;
      }
      // Succès : oauthLoading est reset via onAuthStateChange (SIGNED_IN)
      // dans le useEffect au-dessus. La redirection est portée par
      // useAuth() + router.invalidate() (main.tsx).
    } catch (err) {
      setOauthLoading(null);
      // Apple renvoie une erreur avec code 1001 quand l'utilisateur annule.
      // On ne montre pas d'erreur dans ce cas — c'est un choix normal.
      const msg = err instanceof Error ? err.message : String(err);
      if (!/cancel|1001/i.test(msg)) {
        setError(`Erreur Sign in with Apple : ${msg}`);
      }
    }
  }

  // App mobile (Capacitor) : Google/Facebook (et Apple sur Android) restent
  // sur le flow OAuth Browser + Supabase — Google/Meta interdisent l'OAuth
  // dans une WebView embarquée. Sur iOS, Sign in with Apple bascule sur le
  // flow natif AuthenticationServices — cf. handleAppleSignInNativeIOS.
  // Import dynamique : cette fonction n'est appelée que sur natif, donc le web
  // n'a jamais besoin que @capacitor/browser soit installé.
  async function handleOAuthNative(provider: OAuthProvider) {
    if (provider === 'apple' && Capacitor.getPlatform() === 'ios') {
      await handleAppleSignInNativeIOS();
      return;
    }
    const { Browser } = await import('@capacitor/browser');
    const { data, error: err } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: NATIVE_OAUTH_REDIRECT_URL, skipBrowserRedirect: true },
    });
    if (err || !data?.url) {
      setOauthLoading(null);
      return;
    }
    // Si l'utilisateur ferme le navigateur système sans terminer le flow
    // (annulation), on lève le loading plutôt que de bloquer le bouton.
    const listener = await Browser.addListener('browserFinished', () => {
      setOauthLoading(null);
      void listener.remove();
    });
    await Browser.open({ url: data.url });
  }

  async function handleOAuth(provider: OAuthProvider) {
    setOauthLoading(provider);
    if (Capacitor.isNativePlatform()) {
      await handleOAuthNative(provider);
      return;
    }
    // Slash final NON négociable : l'allow-list Supabase est déclarée en
    // `https://app.jeraisonne.fr/**`, motif que l'origine nue ne matche pas.
    // Sans lui, Supabase ignore silencieusement `redirectTo` et renvoie sur la
    // Site URL — l'utilisateur atterrit ailleurs que dans l'app.
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${window.location.origin}/` },
    });
    if (err) setOauthLoading(null);
  }

  const anyLoading = loading || !!oauthLoading;

  const defaultHeader =
    step === 'otp' ? (
      <>
        <CardTitle>Vérifie ta boîte mail</CardTitle>
        <CardDescription>
          Entre le code à 6 chiffres envoyé à <strong className="text-ink-700">{email}</strong>.
        </CardDescription>
      </>
    ) : mode === 'login' ? (
      <>
        <CardTitle>Connexion ou inscription 👋</CardTitle>
        <CardDescription>
          Entre ton email : si tu n'as pas encore de compte, on le crée automatiquement.
        </CardDescription>
      </>
    ) : (
      <>
        <CardTitle>Créer un compte</CardTitle>
        <CardDescription>
          Rejoins Jeraisonne et commence à progresser dès maintenant.
        </CardDescription>
      </>
    );

  return (
    <>
      <CardHeader>{renderHeader ? renderHeader({ step, mode, email }) : defaultHeader}</CardHeader>

      <CardContent>
        {step === 'email' ? (
          <div className="space-y-6">
            {!hideOAuth && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <SocialButton
                    provider="google"
                    loading={oauthLoading === 'google'}
                    disabled={anyLoading && oauthLoading !== 'google'}
                    onClick={() => handleOAuth('google')}
                  />
                  <SocialButton
                    provider="facebook"
                    loading={oauthLoading === 'facebook'}
                    disabled={anyLoading && oauthLoading !== 'facebook'}
                    onClick={() => handleOAuth('facebook')}
                  />
                  <SocialButton
                    provider="apple"
                    className="col-span-2"
                    loading={oauthLoading === 'apple'}
                    disabled={anyLoading && oauthLoading !== 'apple'}
                    onClick={() => handleOAuth('apple')}
                  />
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="border-ink-200 w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="text-ink-500 bg-white px-3 font-semibold">ou par email</span>
                  </div>
                </div>
              </>
            )}

            <form onSubmit={handleSendOtp} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email" required>
                  Adresse email
                </Label>
                <Input
                  id="email"
                  type="email"
                  required
                  // Pas d'autofocus sur tactile : le clavier virtuel s'ouvrirait
                  // à l'arrivée sur la page et décalerait tout le layout.
                  autoFocus={!lockedEmail && !isTouchPrimary()}
                  autoComplete="email"
                  placeholder="prenom@exemple.fr"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  invalid={!!error}
                  disabled={anyLoading || forcedSignup}
                  readOnly={forcedSignup}
                />
              </div>

              {error && (
                <Alert variant="error">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={loading}
                disabled={!!oauthLoading}
              >
                Recevoir mon code
              </Button>
              <p className="text-ink-500 text-center text-xs">
                Tu recevras un code à 6 chiffres par email. Pas encore de compte ? Il sera créé
                automatiquement, sans mot de passe.
              </p>
            </form>
          </div>
        ) : (
          <div className="space-y-6">
            <OtpInput
              length={6}
              value={otp}
              onChange={setOtp}
              onComplete={handleVerifyOtp}
              disabled={loading}
              autoFocus
            />

            {error && (
              <Alert variant="error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {resentMessage && (
              <Alert variant="info">
                <AlertDescription>{resentMessage}</AlertDescription>
              </Alert>
            )}

            {sentAt != null && (
              <p className="text-ink-500 text-center text-xs" data-testid="otp-sent-at">
                Code envoyé à {otpTime(sentAt)}, valable jusqu’à {otpTime(sentAt + OTP_VALID_MS)}.
                Tu peux aller le lire dans ta boîte mail : en revenant, tu retrouveras cet écran.
              </p>
            )}

            <Button
              type="button"
              variant="primary"
              size="lg"
              fullWidth
              loading={loading}
              disabled={otp.length !== 6}
              onClick={() => handleVerifyOtp(otp)}
            >
              Vérifier le code
            </Button>

            <div className="text-ink-500 text-center text-sm">
              Tu n'as rien reçu ?{' '}
              <button
                type="button"
                onClick={handleResend}
                disabled={isResending || loading}
                className="text-primary-600 font-semibold hover:underline disabled:opacity-50"
              >
                {isResending ? 'Envoi…' : 'Renvoyer le code'}
              </button>
            </div>

            {!forcedSignup && (
              <button
                type="button"
                onClick={() => {
                  // Le code en attente est gardé : revenir à la même adresse ne l'annulera pas.
                  setSentAt(null);
                  setStep('email');
                  setError(null);
                  setOtp('');
                  setResentMessage(null);
                }}
                className="text-ink-500 w-full text-center text-sm hover:underline"
              >
                ← Changer d'adresse email
              </button>
            )}
          </div>
        )}

        {/* Conteneur Turnstile (monté aux 2 étapes : le renvoi du code en a aussi besoin).
            Vide sauf si Cloudflare exige une interaction. */}
        <div ref={turnstile.containerRef} className="mt-4 flex justify-center empty:hidden" />

        <p className="text-ink-500 mt-6 text-center text-xs">
          En continuant, tu acceptes nos{' '}
          <a
            href={`${MARKETING_URL}/cgu`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:underline"
          >
            CGU
          </a>{' '}
          et notre{' '}
          <a
            href={`${MARKETING_URL}/confidentialite`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:underline"
          >
            politique de confidentialité
          </a>
          .
        </p>
      </CardContent>
    </>
  );
}
